import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';
import ViewLayout from '../../../components/Common/ViewLayout';

interface OccurrenceDetail {
  id: number;
  titulo: string;
  descricao: string;
  tipo: string;
  gravidade: string;
  status: string;
  data_ocorrencia: string;
  data_resolucao: string | null;
  veiculo_id: number | null;
  motorista_id: number | null;
  usuario_registro_id: number | null;
  local: string | null;
  responsavel: string | null;
  observacoes_resolucao: string | null;
  fotos: any | null;
  empresa_id: number;
  criado_em: string;
  atualizado_em: string;
  // Campos adicionais do frontend (vindos de joins)
  veiculo_placa?: string;
  motorista_nome?: string;
  usuario_registro_nome?: string;
}

const OccurrenceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [occurrence, setOccurrence] = useState<OccurrenceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOccurrence = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.getOccurrence(Number(id)) as ApiResponse<any>;

      if (response.success && response.data) {
        setOccurrence(response.data);
      } else {
        throw new Error(response.message || 'Erro ao carregar ocorrência');
      }
    } catch (error) {
      console.error('Erro ao carregar ocorrência:', error);
      setError('Erro ao carregar detalhes da ocorrência');
      utils.showNotification('Erro ao carregar detalhes da ocorrência', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOccurrence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    const statusMap: Record<string, string> = {
      'pendente': 'status-danger',
      'em_andamento': 'status-warning',
      'em andamento': 'status-warning',
      'resolvida': 'status-success'
    };
    return statusMap[statusLower] || statusMap[status] || 'status-pending';
  };

  const formatStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      'pendente': 'Pendente',
      'em_andamento': 'Em Andamento',
      'resolvida': 'Resolvida'
    };
    return statusMap[status] || status;
  };

  const getSeverityClass = (gravidade: string): string => {
    const gravidadeLower = gravidade.toLowerCase();
    const severityMap: Record<string, string> = {
      'baixa': 'status-info',
      'media': 'status-warning',
      'média': 'status-warning',
      'alta': 'status-danger'
    };
    return severityMap[gravidadeLower] || severityMap[gravidade] || 'status-secondary';
  };

  const formatGravidade = (gravidade: string): string => {
    const gravidadeMap: Record<string, string> = {
      'baixa': 'Baixa',
      'media': 'Média',
      'média': 'Média',
      'alta': 'Alta'
    };
    return gravidadeMap[gravidade] || gravidade;
  };

  const getResolutionTime = (): string => {
    if (!occurrence || !occurrence.data_resolucao) return '-';

    const startDate = new Date(occurrence.data_ocorrencia);
    const endDate = new Date(occurrence.data_resolucao);
    const diffMs = endDate.getTime() - startDate.getTime();

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ${diffHours}h ${diffMinutes}min`;
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}min`;
    return `${diffMinutes}min`;
  };

  if (isLoading) {
    return (
      <ViewLayout
        title="Carregando Ocorrência..."
        isLoading={true}
        onBack={() => navigate('/ocorrencias')}
      >
        <div></div>
      </ViewLayout>
    );
  }

  if (error || !occurrence) {
    return (
      <ViewLayout
        title="Erro ao Carregar"
        onBack={() => navigate('/ocorrencias')}
      >
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
            <h3>{error || 'Ocorrência não encontrada'}</h3>
            <p>A ocorrência solicitada não pôde ser carregada.</p>
            <button className="btn btn-primary" onClick={() => navigate('/ocorrencias')}>
              Voltar para Lista
            </button>
          </div>
        </div>
      </ViewLayout>
    );
  }

  const resolutionTime = getResolutionTime();

  return (
    <ViewLayout
      title="Detalhes da Ocorrência"
      subtitle={occurrence.titulo}
      entityId={occurrence.id.toString()}
      onBack={() => navigate('/ocorrencias')}
    >
      <div className="card">
        <div className="card-body">
          {/* Informações Básicas */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações Básicas
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Título</label>
                <span className="info-value">{occurrence.titulo}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Tipo</label>
                <span className="info-value">
                  <span className="status-badge status-info">
                    {occurrence.tipo}
                  </span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Gravidade</label>
                <span className="info-value">
                  <span className={`status-badge ${getSeverityClass(occurrence.gravidade)}`}>
                    {formatGravidade(occurrence.gravidade)}
                  </span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Status</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(occurrence.status)}`}>
                    {formatStatus(occurrence.status)}
                  </span>
                </span>
              </div>

              <div className="info-item">
                <label className="form-label">Data e Hora da Ocorrência</label>
                <span className="info-value">{formatDateTime(occurrence.data_ocorrencia)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Local</label>
                <span className="info-value">{occurrence.local || '-'}</span>
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Descrição da Ocorrência
            </h3>

            <div className="info-item">
              <label className="form-label">Descrição</label>
              <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {occurrence.descricao}
              </p>
            </div>
          </div>

          {/* Envolvidos */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Envolvidos
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">Veículo</label>
                <span className="info-value">{occurrence.veiculo_placa || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Motorista</label>
                <span className="info-value">{occurrence.motorista_nome || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Responsável pela Resolução</label>
                <span className="info-value">{occurrence.responsavel || '-'}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Registrado por</label>
                <span className="info-value">{occurrence.usuario_registro_nome || '-'}</span>
              </div>
            </div>
          </div>

          {/* Resolução (apenas se resolvida) */}
          {occurrence.status?.toLowerCase() === 'resolvida' && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Resolução
              </h3>

              <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="info-item">
                  <label className="form-label">Data de Resolução</label>
                  <span className="info-value">{formatDateTime(occurrence.data_resolucao)}</span>
                </div>

                <div className="info-item">
                  <label className="form-label">Tempo de Resolução</label>
                  <span className="info-value">{resolutionTime}</span>
                </div>
              </div>

              {occurrence.observacoes_resolucao && (
                <div className="info-item" style={{ marginTop: '20px' }}>
                  <label className="form-label">Observações da Resolução</label>
                  <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0, padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    {occurrence.observacoes_resolucao}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Fotos */}
          {occurrence.fotos && Array.isArray(occurrence.fotos) && occurrence.fotos.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Fotos da Ocorrência
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                {occurrence.fotos.map((foto: any, index: number) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img
                      src={foto.url || foto}
                      alt={`Foto ${index + 1} da ocorrência`}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(foto.url || foto, '_blank')}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Informações de Auditoria */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações de Auditoria
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label className="form-label">ID da Ocorrência</label>
                <span className="info-value">#{occurrence.id}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Criado em</label>
                <span className="info-value">{formatDateTime(occurrence.criado_em)}</span>
              </div>

              <div className="info-item">
                <label className="form-label">Atualizado em</label>
                <span className="info-value">{formatDateTime(occurrence.atualizado_em)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ViewLayout>
  );
};

export default OccurrenceView;
